/**
 * Botão de microfone padronizado: mesmo comportamento em todo o sistema.
 * - Verde pulsando quando gravando
 * - Timer de 10 min
 * - Ao clicar em outro mic na mesma tela, para o anterior e preserva texto
 * - useController integrado
 */

import { useEffect } from 'react'
import { MicIcon } from '@/shared/ui/icons'
import { iconButtonTouchClasses } from '@/shared/ui/Button'
import { focusRingClasses } from '@/shared/lib/focusRing'
import { useSpeechToText } from '@/shared/lib/useSpeechToText'
import { cn } from '@/shared/lib/cn'
import { Tooltip } from '@/shared/ui/Tooltip'

const ICON_SIZE = 24

export interface MicButtonProps {
  onTranscript: (text: string) => void
  /** streaming: envia a cada frase final (interim no motor). onStop: envia só ao parar o mic (padrão). */
  mode?: 'streaming' | 'onStop'
  /** Integrar com controlador global (apenas um mic ativo por vez). */
  useController?: boolean
  className?: string
  /** Posição: bottom-right (TextArea), top-right (TextInput). */
  position?: 'bottom-right' | 'top-right'
  /** Expor stop para o pai (ex.: form submit). */
  onStopRef?: React.MutableRefObject<(() => void) | null>
  /** Callback para exibir erro no pai. */
  onErrorChange?: (error: string | null) => void
  /** Inerte: sem gravação nem toggle (defesa em profundidade). */
  disabled?: boolean
}

export function MicButton({
  onTranscript,
  mode = 'onStop',
  useController = true,
  className,
  position = 'bottom-right',
  onStopRef,
  onErrorChange,
  disabled = false,
}: MicButtonProps) {
  const { isListening, error, toggle, stop, isSupported } = useSpeechToText({
    onTranscript: disabled ? () => {} : onTranscript,
    mode,
    useController: disabled ? false : useController,
  })

  useEffect(() => {
    onErrorChange?.(error)
  }, [error, onErrorChange])

  useEffect(() => {
    if (!onStopRef) return
    onStopRef.current = stop
    return () => {
      if (onStopRef.current === stop) onStopRef.current = null
    }
  }, [onStopRef, stop])

  if (!isSupported) return null

  const positionClasses =
    position === 'bottom-right'
      ? 'absolute bottom-2 right-2'
      : 'absolute right-2 top-1/2 -translate-y-1/2'

  const label = isListening ? 'Parar gravação' : 'Falar (ditado por voz)'

  return (
    <Tooltip label={label}>
    <button
      type="button"
      onClick={disabled ? undefined : toggle}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      className={cn(
        iconButtonTouchClasses,
        focusRingClasses,
        positionClasses,
        disabled
          ? 'pointer-events-none cursor-not-allowed opacity-40'
          : isListening
            ? 'bg-[var(--color-mint-strong)] text-white synap-mic-recording'
            : 'text-[var(--color-text-muted)] hover:bg-[var(--color-mint-soft)] hover:text-[var(--color-teal-ink)]',
        className,
      )}
      aria-label={label}
      aria-pressed={isListening}
    >
      <MicIcon size={ICON_SIZE} aria-hidden />
    </button>
    </Tooltip>
  )
}